import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, Plus, Edit, Trash2, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { payrollPaymentService } from '@/services/payrollPaymentService'
import type { PayrollPaymentListItem } from '@/api/types/payroll.types'

function formatMonthYear(ym?: string, locale?: string): string {
  if (!ym || ym.length < 5) return '—'
  const parts = ym.split('-').filter(Boolean)
  if (parts.length >= 2) {
    let monthNum: number
    let yearStr: string
    if (parts[0].length === 4) {
      yearStr = parts[0]
      monthNum = parseInt(parts[1], 10)
    } else {
      monthNum = parseInt(parts[0], 10)
      yearStr = parts[1]
    }
    if (monthNum >= 1 && monthNum <= 12 && yearStr) {
      const loc = locale?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
      return new Date(Number(yearStr), monthNum - 1, 1).toLocaleDateString(loc, { month: 'long', year: 'numeric' })
    }
  }
  return ym
}

function formatDate(dateString?: string, locale?: string): string {
  if (!dateString) return '—'
  try {
    const loc = locale?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
    const d = new Date(dateString)
    return d.toLocaleDateString(loc, { year: 'numeric', month: 'short', day: '2-digit' })
  } catch {
    return String(dateString).slice(0, 10) || '—'
  }
}

export const PayrollPaymentList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [rows, setRows] = useState<PayrollPaymentListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)

  useEffect(() => {
    if (!branchId) {
      setRows([])
      setTotalRecords(0)
      return
    }
    let cancelled = false
    setLoading(true)
    payrollPaymentService
      .getAll(branchId, pageNumber, pageSize)
      .then((res) => {
        if (!cancelled) {
          setRows(res.data ?? [])
          setTotalRecords(res.totalRecords ?? 0)
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setRows([])
          setTotalRecords(0)
          const msg = (err.response?.data?.message || err.message || '').toLowerCase()
          if (msg.includes('no payroll payment') || msg.includes('not found') || msg.includes('no data')) {
            return
          }
          toast.error(err.response?.data?.message || err.message || t('payrollPaymentPage.fetchFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [branchId, pageNumber, pageSize])

  const filteredRows = React.useMemo(() => {
    if (!search.trim()) return rows
    const q = search.toLowerCase()
    return rows.filter(
      (r) =>
        (r.paymentNo ?? '').toLowerCase().includes(q) ||
        formatMonthYear(r.payrollMonthYear, locale).toLowerCase().includes(q) ||
        formatDate(r.date ?? r.paymentDate, locale).toLowerCase().includes(q) ||
        String(r.amount ?? '').includes(q)
    )
  }, [rows, search, locale])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))

  const handleDelete = async (row: PayrollPaymentListItem) => {
    if (!row.id) return
    const ok = await showDeleteConfirm(undefined, t('payrollPaymentPage.deleteConfirm'))
    if (!ok) return
    try {
      await payrollPaymentService.delete(row.id)
      await showSuccess(t('payrollPaymentPage.deletedTitle'), t('payrollPaymentPage.deletedMessage'), 2000)
      setRows((prev) => prev.filter((r) => r.id !== row.id))
      setTotalRecords((prev) => Math.max(0, prev - 1))
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('payrollPaymentPage.deleteFailed'))
    }
  }

  const pagFrom = totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const pagTo = totalRecords === 0 ? 0 : Math.min(pageNumber * pageSize, totalRecords)

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('payrollPaymentPage.pageTitle')} description={t('payrollPaymentPage.listDescription')} />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t('payrollPaymentPage.selectBranchList')}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title={t('payrollPaymentPage.pageTitle')} description={t('payrollPaymentPage.listDescription')}>
        <Button variant="default" onClick={() => navigate('/payroll/payroll-payment/new')}>
          <Plus className="w-4 h-4 mr-2" />
          {t('payrollPaymentPage.addButton')}
        </Button>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>{t('payrollPaymentPage.cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('payrollPaymentPage.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <div className="rounded-md border overflow-x-auto">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-center">{t('payrollPaymentPage.columnPaymentNo')}</TableHead>
                    <TableHead className="text-center">{t('payrollPaymentPage.columnMonthYear')}</TableHead>
                    <TableHead className="text-center">{t('payrollPaymentPage.columnPaymentDate')}</TableHead>
                    <TableHead className="text-center">{t('payrollPaymentPage.columnAmount')}</TableHead>
                    <TableHead className="text-center">{t('payrollPaymentPage.columnAction')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        {t('payrollPaymentPage.empty')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="text-center">{row.paymentNo ?? '—'}</TableCell>
                        <TableCell className="text-center">{formatMonthYear(row.payrollMonthYear, locale)}</TableCell>
                        <TableCell className="text-center">{formatDate(row.date ?? row.paymentDate, locale)}</TableCell>
                        <TableCell className="text-center">{row.amount ?? '—'}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/payroll/payroll-payment/${row.id}/edit`)}
                              title={t('payrollPaymentPage.editTitle')}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(row)}
                              title={t('payrollPaymentPage.deleteTitle')}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
            <span>
              {t('payrollPaymentPage.paginationShowing', { from: pagFrom, to: pagTo, total: totalRecords })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              >
                {t('payrollPaymentPage.previous')}
              </Button>
              <span>{t('payrollPaymentPage.pageOf', { page: pageNumber, totalPages })}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber >= totalPages}
                onClick={() => setPageNumber((p) => p + 1)}
              >
                {t('payrollPaymentPage.next')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
